import React from 'react';
import TailwindButton from '../../Tailwind/TailwindButton';
import { ConnectionDetails, SalesforceFormData, SalesforceConnectionDetails } from '../types';
import { ERP_TYPES } from '../constants';
import SalesforceForm from '../forms/SalesforceForm';

interface DetailsViewProps {
    connection: ConnectionDetails | null;
    connectionType: string;
    isAdmin: boolean;
    isEditing: boolean;
    isNewlyCreated: boolean;
    loading: boolean;
    saveLoading: boolean;
    saveError: string | null;
    saveSuccess: boolean;
    testLoading: boolean;
    testResult: { status: boolean } | null;
    deleteLoading: boolean;
    showDeleteConfirm: boolean;
    salesforceForm: SalesforceFormData;
    setSalesforceForm: React.Dispatch<React.SetStateAction<SalesforceFormData>>;
    privateKeyInputMode: 'text' | 'file';
    setPrivateKeyInputMode: (mode: 'text' | 'file') => void;
    setSaveError: (error: string | null) => void;
    setSaveSuccess: (success: boolean) => void;
    setTestResult: (result: { status: boolean } | null) => void;
    setShowDeleteConfirm: (show: boolean) => void;
    onBack: () => void;
    onStartEditing: () => void;
    onSave: () => void;
    onCancelEdit: () => void;
    onTest: () => void;
    onDelete: () => void;
}

export default function DetailsView({
    connection,
    connectionType,
    isAdmin,
    isEditing,
    isNewlyCreated,
    loading,
    saveLoading,
    saveError,
    saveSuccess,
    testLoading,
    testResult,
    deleteLoading,
    showDeleteConfirm,
    salesforceForm,
    setSalesforceForm,
    privateKeyInputMode,
    setPrivateKeyInputMode,
    setSaveError,
    setSaveSuccess,
    setTestResult,
    setShowDeleteConfirm,
    onBack,
    onStartEditing,
    onSave,
    onCancelEdit,
    onTest,
    onDelete,
}: DetailsViewProps) {
    const config = ERP_TYPES[connectionType];

    return (
        <div className="space-y-4">
            {/* Back link */}
            <button
                onClick={onBack}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
                <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                Back to list
            </button>

            {/* Action buttons (non-editing) */}
            {!isEditing && isAdmin && (
                <div className="flex items-center gap-2 flex-wrap">
                    <TailwindButton
                        variant="secondary"
                        size="sm"
                        icon={testLoading ? undefined : 'play_arrow'}
                        onClick={onTest}
                        disabled={testLoading}
                    >
                        {testLoading ? (
                            <span className="flex items-center gap-2">
                                <span className="size-3.5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                                Testing...
                            </span>
                        ) : (
                            'Test'
                        )}
                    </TailwindButton>
                    <TailwindButton
                        variant="secondary"
                        size="sm"
                        icon="edit"
                        onClick={onStartEditing}
                    >
                        Edit
                    </TailwindButton>
                    <TailwindButton
                        variant="danger"
                        size="sm"
                        icon="delete"
                        onClick={() => setShowDeleteConfirm(true)}
                    >
                        Delete
                    </TailwindButton>
                </div>
            )}

            {/* Delete confirmation */}
            {showDeleteConfirm && (
                <div className="flex items-center justify-between gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[20px] text-amber-600">warning</span>
                        <p className="text-sm text-amber-800">
                            Are you sure? This action cannot be undone.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <TailwindButton
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </TailwindButton>
                        <TailwindButton
                            variant="danger"
                            size="sm"
                            onClick={onDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? (
                                <span className="flex items-center gap-2">
                                    <span className="size-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Deleting...
                                </span>
                            ) : (
                                'Confirm Delete'
                            )}
                        </TailwindButton>
                    </div>
                </div>
            )}

            {/* Success alert */}
            {saveSuccess && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                        {isNewlyCreated ? 'Connection created successfully' : 'Connection saved successfully'}
                    </div>
                    <button onClick={() => setSaveSuccess(false)} className="text-green-400 hover:text-green-600 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            {/* Error alert */}
            {saveError && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">error</span>
                        {saveError}
                    </div>
                    <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            {/* Test result */}
            {testResult && (
                <div className={`flex items-center justify-between gap-2 px-4 py-3 rounded-lg text-sm border ${
                    testResult.status
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">
                            {testResult.status ? 'check_circle' : 'error'}
                        </span>
                        {testResult.status ? 'Connection test successful' : 'Connection test failed'}
                    </div>
                    <button onClick={() => setTestResult(null)} className="opacity-50 hover:opacity-100 transition-opacity">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            {/* Loading state */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                        <div className="size-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-slate-400">Loading details...</p>
                    </div>
                </div>
            ) : connection ? (
                <div>
                    {/* Connection type badge */}
                    {config && (
                        <div className="flex items-center gap-2 mb-4">
                            <img
                                src={config.logo}
                                alt={config.label}
                                className="h-6 object-contain"
                            />
                            <span className="text-sm font-medium text-slate-500">{config.label}</span>
                        </div>
                    )}

                    {connectionType === 'salesforce' && (
                        <SalesforceForm
                            form={salesforceForm}
                            setForm={setSalesforceForm}
                            isEditing={isEditing}
                            connection={connection as SalesforceConnectionDetails}
                            privateKeyInputMode={privateKeyInputMode}
                            setPrivateKeyInputMode={setPrivateKeyInputMode}
                        />
                    )}

                    {/* Edit mode actions */}
                    {isEditing && (
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                            <TailwindButton
                                variant="ghost"
                                onClick={onCancelEdit}
                                disabled={saveLoading}
                            >
                                Cancel
                            </TailwindButton>
                            <TailwindButton
                                variant="primary"
                                icon={saveLoading ? undefined : 'save'}
                                onClick={onSave}
                                disabled={saveLoading}
                            >
                                {saveLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Saving...
                                    </span>
                                ) : (
                                    'Save Changes'
                                )}
                            </TailwindButton>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="size-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                        <span className="material-symbols-outlined text-[32px] text-slate-300">link_off</span>
                    </div>
                    <p className="text-sm font-medium text-slate-500">Connection not found</p>
                </div>
            )}
        </div>
    );
}
